import type { ContentType } from "@/config/brand";
import type { Author, Post, SEOMeta, Section } from "@/types/content";

const CONTENT_TYPES: ContentType[] = [
  "multiShopReview",
  "singleShopReview",
  "guide",
  "lazyPack",
  "story",
];

function asContentType(value: unknown): ContentType | undefined {
  return typeof value === "string" && CONTENT_TYPES.includes(value as ContentType)
    ? (value as ContentType)
    : undefined;
}

function isSectionLike(value: unknown): value is { _type: string; _key: string } {
  if (!value || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return typeof o._type === "string" && typeof o._key === "string";
}

function parseSectionsFromUnknown(value: unknown): Section[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isSectionLike) as Section[];
}

function parseLegacyContentJson(raw: unknown): Section[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return parseSectionsFromUnknown(parsed);
  } catch {
    return [];
  }
}

function pickAuthor(raw: unknown): Author | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" ? o.id : typeof o._id === "string" ? o._id : "";
  const name = typeof o.name === "string" ? o.name : "";
  if (!id || !name) return undefined;
  return {
    id,
    name,
    avatar: typeof o.avatar === "string" ? o.avatar : "",
    bio: typeof o.bio === "string" ? o.bio : undefined,
  };
}

function pickSeo(raw: unknown): SEOMeta | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const title = typeof o.title === "string" ? o.title : "";
  const description = typeof o.description === "string" ? o.description : "";
  if (!title || !description) return undefined;
  const seo: SEOMeta = { title, description };
  const og = o.ogImageUrl ?? o.ogImage;
  if (typeof og === "string" && og) seo.ogImage = og;
  if (typeof o.canonical === "string" && o.canonical) seo.canonical = o.canonical;
  if (Array.isArray(o.keywords) && o.keywords.every((k) => typeof k === "string")) {
    seo.keywords = o.keywords as string[];
  }
  if (o.structuredData != null) {
    if (typeof o.structuredData === "object" && !Array.isArray(o.structuredData)) {
      seo.structuredData = o.structuredData as Record<string, unknown>;
    } else if (typeof o.structuredData === "string" && o.structuredData.trim()) {
      try {
        const parsed: unknown = JSON.parse(o.structuredData);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          seo.structuredData = parsed as Record<string, unknown>;
        }
      } catch {
        /* ignore */
      }
    }
  }
  return seo;
}

/** 將 Sanity GROQ 結果轉成前端 `Post`；失敗回 `null` */
export function sanityDocToPost(raw: unknown): Post | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o._id === "string" ? o._id : "";
  const slug = typeof o.slug === "string" ? o.slug : "";
  const title = typeof o.title === "string" ? o.title : "";
  if (!id || !slug || !title) return null;

  const excerpt = typeof o.excerpt === "string" ? o.excerpt : "";
  const category = typeof o.category === "string" ? o.category : "未分類";
  const tags = Array.isArray(o.tags) ? o.tags.filter((t): t is string => typeof t === "string") : [];
  const coverImage =
    typeof o.coverImage === "string" && o.coverImage
      ? o.coverImage
      : "https://placehold.co/1200x630/e2e8f0/64748b?text=Post";
  const publishedAt =
    typeof o.publishedAt === "string" && o.publishedAt
      ? o.publishedAt
      : new Date().toISOString().slice(0, 10);
  const readingTime = typeof o.readingTime === "number" ? o.readingTime : 5;
  const rating = typeof o.rating === "number" ? o.rating : undefined;
  const reviewCount = typeof o.reviewCount === "number" ? o.reviewCount : undefined;
  const contentType = asContentType(o.contentType);
  const content = parseLegacyContentJson(o.legacyContentJson);
  const author = pickAuthor(o.author);
  const seo = pickSeo(o.seo);

  const post: Post = {
    id,
    slug,
    title,
    excerpt,
    coverImage,
    category,
    tags,
    publishedAt,
    readingTime,
    content,
    ...(contentType ? { contentType } : {}),
    ...(typeof rating === "number" ? { rating } : {}),
    ...(typeof reviewCount === "number" ? { reviewCount } : {}),
    ...(author ? { author } : {}),
    ...(seo ? { seo } : {}),
  };
  return post;
}
